/**
 * TypeSelection Component
 * 
 * First step in the AlertForm wizard that allows users to select the type of alert they want to create.
 * Displays cards for each alert type with descriptions and icons.
 * Auto-advances to the next step after selection.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { 
  AlertType, 
  getAlertTypeDisplayText, 
  getAlertTypeIcon 
} from '../../../models/AlertModels';

/**
 * TypeSelection component for selecting the alert type
 * 
 * @returns {JSX.Element} The TypeSelection component
 */
function TypeSelection() {
  const { formState, updateFormState } = useAlertContext();
  // Add null check to prevent crash when formState.alert is null
  const [selectedType, setSelectedType] = useState(formState?.alert?.type || null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Get alert data with null checks
  const alert = formState?.alert || {};
  const meta = alert.meta || {};
  const isEditMode = formState.isEditing;
  
  // Check for process-initiated flags in both root level and meta
  const isProcessInitiated = 
    meta.initiatedFromProcess === true || 
    alert.isProcessInitiated === true;
  
  // Lock the alert type if editing or process-initiated
  const lockAlertType = 
    isEditMode || 
    isProcessInitiated || 
    meta.lockAlertType === true;
  


  // If there's already a type selected when mounting (edit mode), set it
  // This hook must be placed before any conditional returns
  useEffect(() => {
    // Add null checks to prevent crash when accessing properties
    if (formState?.alert?.type && !selectedType) {
      setSelectedType(formState.alert.type);
    }
  }, [formState?.alert?.type, selectedType]);
  
  // Handle cases where formState or formState.alert might be null
  if (!formState || !formState.alert) {
    // Return a loading state or redirect back to the first step
    return (
      <div className="type-selection-container">
        <h3 className="step-title">Loading Alert Types...</h3>
        <p>Please wait while we load the available alert types.</p>
      </div>
    );
  }
  

  /**
   * Handle alert type selection
   * 
   * @param {string} type - The selected alert type
   */
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setIsTransitioning(true);
    
    // If the type is changing, clear previous rules/events
    const shouldClearRules = formState.alert.type && formState.alert.type !== type;
    
    // Update the alert state
    const updatedAlert = {
      ...formState.alert,
      type,
      // Clear rules if type is changing
      rules: shouldClearRules ? [] : formState.alert.rules
    };
    
    updateFormState({
      alert: updatedAlert
    });
    
    // Auto-advance to next step after a short delay
    setTimeout(() => {
      updateFormState({
        currentStep: formState.currentStep + 1
      });
    }, 500); // Short delay for visual feedback
  };
  
  // Alert type descriptions
  const typeDescriptions = {
    [AlertType.GLOBAL]: 'Monitor connection status events such as WebSocket connections and server connections.',
    [AlertType.SYSTEM_EVENT]: 'Monitor system-wide metrics like CPU utilization, memory usage, disk space, and network traffic.',
    [AlertType.PROCESS]: 'Monitor process-specific metrics such as memory consumption, CPU utilization, and disk usage.',
    [AlertType.LOG]: 'Monitor log entries based on log level or message content.'
  };
  
  // Available alert types
  const alertTypes = [
    AlertType.GLOBAL,
    AlertType.SYSTEM_EVENT,
    AlertType.PROCESS,
    AlertType.LOG
  ];
  
  return (
    <div className="type-selection-container">
      <h3 className="step-title">Select Alert Type</h3>
      <p className="step-description">
        {lockAlertType 
          ? isProcessInitiated
            ? 'This alert was initiated from the Process panel. Alert type is set to Process.'
            : 'Alert type cannot be changed in edit mode. The current alert type is shown below.'
          : 'Choose the type of alert you want to create. Different alert types monitor different aspects of your system.'}
      </p>
      
      {lockAlertType && (
        <div className="edit-mode-notice" style={{
          background: 'rgba(0, 0, 0, 0.05)', 
          padding: '10px 15px',
          borderRadius: '4px',
          marginBottom: '15px',
          borderLeft: '4px solid #4a90e2',
          color: '#666'
        }}>
          <i className="fa fa-info-circle" style={{ marginRight: '8px' }}></i>
          {isProcessInitiated
            ? `This alert is being created for process: ${formState.alert.meta?.targetProcessName || 'Unknown'}`
            : 'Alert type cannot be changed after creation. To use a different alert type, create a new alert instead.'}
        </div>
      )}
      
      <div className={`alert-type-grid ${isTransitioning ? 'transitioning' : ''}`}>
        {alertTypes.map((type) => (
          <div 
            key={type}
            className={`alert-type-card ${selectedType === type ? 'selected' : ''} ${lockAlertType && type !== selectedType ? 'disabled' : ''}`}
            onClick={() => !isTransitioning && !lockAlertType && handleTypeSelect(type)}
            onKeyPress={(e) => e.key === 'Enter' && !isTransitioning && !lockAlertType && handleTypeSelect(type)}
            tabIndex={lockAlertType && type !== selectedType ? -1 : 0}
            role="button"
            aria-pressed={selectedType === type}
            aria-disabled={lockAlertType && type !== selectedType}
            aria-label={`Select ${getAlertTypeDisplayText(type)} alert type`}
            style={lockAlertType && type !== selectedType ? { 
              opacity: 0.6, 
              cursor: 'not-allowed',
              filter: 'grayscale(60%)',
              border: '1px solid #ddd'
            } : {}}
          >
            <div className="alert-type-icon">
              {getAlertTypeIcon(type)}
            </div>
            <div className="alert-type-content">
              <h4 className="alert-type-title">{getAlertTypeDisplayText(type)}</h4>
              <p className="alert-type-description">{typeDescriptions[type]}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TypeSelection;
